'use client';

import { ElementProps } from '@/components/sections/builder/types';
import React from 'react';
import {
  ProductDetailQuantityElementDataType,
  ProductDetailTitleElementDataType,
} from '@/components/sections/builder/elements/body/product-detail/type';
import { Hash } from 'lucide-react';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/vanilla/shallow';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { IProduct } from '@/types/product';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCartProduct } from '@/stores/cart';
import { Input } from '@/components/ui/input';
import { useProductStore } from '@/stores/product';
import { cn } from '@/lib/utils';

const defaultData: ProductDetailQuantityElementDataType = {
  name: 'Quantity',
  qty: 0,
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const parentData = (element.parentData || {}) as IProduct;
  const data = (element.data || {}) as ProductDetailTitleElementDataType;

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );
  const cartProduct = useCartProduct((state) => state, shallow);
  const productStore = useProductStore((state) => state, shallow);

  const themeContent = preview ? builder.theme : element.theme;

  const availableQty = parentData.available_qty || 0;
  const productVariants = parentData.variants;
  const quantity = productStore.quantity;
  const cartQty = cartProduct.quantity;

  const incrementQty = React.useCallback(() => {
    const newQty = cartQty + 1;
    if (newQty <= availableQty) {
      cartProduct.setQuantity(newQty);
    }
  }, [cartQty, availableQty, cartProduct]);
  const decrementQty = React.useCallback(() => {
    const newQty = cartQty - 1;
    if (newQty >= 1) {
      cartProduct.setQuantity(newQty);
    }
  }, [cartQty, cartProduct]);
  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseInt(e.target.value.replace(/\D/g, ''), 10) || 1;
    const maxQty = Math.min(availableQty, quantity);
    const clampedQty = Math.max(1, Math.min(numericValue, maxQty));

    cartProduct.setQuantity(clampedQty);
  };

  React.useEffect(() => {
    if (cartQty === 0) {
      cartProduct.setQuantity(quantity > 0 ? 1 : 0);
    }
  }, [quantity, cartProduct]);

  React.useEffect(() => {
    productStore.setQuantity(availableQty);
  }, [parentData.id]);

  return (
    <>
      {themeContent?.productCard?.positionButtonAddToCart !== 'fixed' ? (
        <div className='flex flex-col'>
          <label className='block mb-1 text-sm font-medium'>Quantity</label>
          <div className='flex items-center mt-2 space-x-2'>
            <div className='flex items-center p-2 space-x-6 border rounded-lg'>
              <Button
                variant='outline'
                size='icon'
                onClick={decrementQty}
                className='theme_button_outline_modal_product_detail disabled:cursor-not-allowed'
                disabled={cartQty <= 1 || !!(productVariants && !productStore.allSelectedVariants)}
              >
                <Minus className='w-2 h-2' />
              </Button>
              <Input
                id='input-qty-product-detail'
                type='text'
                value={cartQty}
                onChange={handleQtyChange}
                max={quantity}
                min={1}
                className={cn(
                  'w-[60px] border-none text-center font-semibold focus-visible:ring-0',
                  data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                  (data.textStyle === 'subtitle' || !data.textStyle) &&
                    'text-sm md:text-base lg:text-lg',
                  data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
                )}
                disabled={cartQty === 0 || !!(productVariants && !productStore.allSelectedVariants)}
              />
              <Button
                variant='outline'
                size='icon'
                onClick={incrementQty}
                className='theme_button_outline_modal_product_detail'
                disabled={
                  cartQty === 0 ||
                  cartQty >= quantity ||
                  !!(productVariants && !productStore.allSelectedVariants)
                }
              >
                <Plus className='w-2 h-2' />
              </Button>
            </div>
          </div>
          <div className='flex gap-2 mt-4 text-sm text-gray-500'>
            {themeContent?.productCard?.showQtySold && (
              <span className='pr-2 border-r border-gray-500'>{parentData?.qty_sold} sold</span>
            )}
            <span>Stok : {quantity > 0 ? quantity : availableQty}</span>
          </div>
        </div>
      ) : (
        <div className='flex gap-2 text-sm text-gray-500'>
          {themeContent?.productCard?.showQtySold && (
            <span className='pr-2 border-r border-gray-500'>{parentData?.qty_sold} sold</span>
          )}
          <span>Stok : {quantity > 0 ? quantity : availableQty}</span>
        </div>
      )}
    </>
  );
};

export const ElementPreviewIcon = Hash;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;

  return (
    <React.Fragment>
      <div className='my-4 font-bold'>Text Style</div>
      <InputTab
        defaultValue={data.textStyle || 'subtitle'}
        options={[
          { label: 'Body', value: 'body' },
          { label: 'Subtitle', value: 'subtitle' },
          { label: 'UPPERCASE', value: 'uppercase' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            textStyle: v,
          })
        }
      />
    </React.Fragment>
  );
}

const ProductDetailQuantity = {
  type: 'qty_button',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailQuantity;
